// Local-safe utilities for date-only values (YYYY-MM-DD strings).
//
// Two timezone pitfalls this module guards against:
//   1. new Date().toISOString().split('T')[0] captures UTC date, which can be
//      tomorrow's date for users east of UTC late in their local day.
//   2. new Date("YYYY-MM-DD") parses as UTC midnight, which converts back to
//      the previous day in any timezone west of UTC (e.g., Pacific).
//
// For full ISO timestamps like "2026-04-16T19:00:00.000Z", new Date() already
// handles the conversion correctly because the time component resolves the TZ.
// Only use these helpers for DATE-ONLY strings.

/** Today's date as "YYYY-MM-DD" using the user's local timezone. */
export function todayLocalYMD(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Parse a "YYYY-MM-DD" string as local midnight (not UTC midnight). */
export function parseLocalYMD(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  // The constructor reads years 0-99 as 1900-1999; setFullYear does not.
  date.setFullYear(y)
  return date
}

/**
 * Format a "YYYY-MM-DD" string for user-facing display, parsed as local.
 * Use this instead of `new Date(ymd).toLocaleDateString()` for date-only fields.
 */
export function formatLocalYMD(
  ymd: string,
  locale?: string,
  opts?: Intl.DateTimeFormatOptions
): string {
  return parseLocalYMD(ymd).toLocaleDateString(locale, opts)
}

// ---------------------------------------------------------------------------
// Full ISO timestamps (not date-only). Everything above this line is for
// "YYYY-MM-DD" values; the helper below is for values that carry a time.
// ---------------------------------------------------------------------------

/**
 * A full ISO timestamp as a `datetime-local` input wants it: local wall-clock
 * time, to the minute, with no zone. Built by hand because toISOString() would
 * shift the value into UTC and show the user a different time than they typed.
 */
export function toDateTimeLocalInput(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
